import { z } from 'zod';
import { openWorldDirectionSchema } from './direction';

/**
 * **Crossing a border between two dangerous quadrants.**
 *
 * ## The one thing this file is about: nobody is ever in two places
 *
 * A quadrant is a Durable Object, and two Durable Objects cannot share a
 * transaction. There is no two-phase commit available, there is no distributed
 * lock, and pretending otherwise would produce a design that works until the
 * first eviction lands between two writes. So a crossing is a **state machine
 * with a single linearisation point**: one authority says where an account is,
 * and its answer is what both rooms obey.
 *
 * Five states, and the boundary between two of them is the whole contract:
 *
 * | state | who owns the hull |
 * | --- | --- |
 * | `prepared` | the ORIGIN |
 * | `destination_ready` | the ORIGIN |
 * | **`committed`** | the DESTINATION, and there is no way back |
 * | `finalized` | the DESTINATION |
 * | `expired` | the ORIGIN, and the reservation is worth nothing |
 *
 * Everything before `committed` can be abandoned at no cost: the hull never
 * left, and the reservation simply runs out. Nothing after it can be undone:
 * the destination owns the hull, and a rollback would mean two rooms each
 * believing they hold the same ship.
 *
 * ## Why the ship stays vulnerable the whole time it is preparing
 *
 * Because a loading screen is not a shield. The alternative - freeze the hull
 * while the destination gets ready - hands every player a button that makes
 * them untouchable for as long as somebody else's server takes to answer, and
 * the first thing anybody would learn is to press it when they are losing.
 *
 * So the hull keeps flying, keeps shooting and keeps taking damage in the
 * origin for the whole of `prepared` and `destination_ready`, and the origin
 * re-checks that it is still alive and still on the border at the moment it
 * asks to commit. A ship that died while preparing simply died.
 *
 * ## What a client may say, and it is three things
 *
 * A direction, an idempotency key, and the sequence its connection was already
 * carrying. It may not name a destination, a position, a participant, a room or
 * a state - all of those are derived by the origin from what it already holds.
 */

/**
 * The handoff contract's format version. **2.**
 *
 * Moved by the collective crossing: an expedition can now cross as one, which
 * added an authority, a proposal, a set of cargos and two refusals. A build
 * speaking version 1 has no shape for any of it.
 */
export const OPEN_WORLD_HANDOFF_VERSION = 3;

/**
 * The five states, in the order a crossing walks them.
 *
 * `expired` is not a sixth step - it is where `prepared` and
 * `destination_ready` go when nobody commits in time. There is no path from
 * `committed` to it.
 */
export const OPEN_WORLD_HANDOFF_STATES = [
  'prepared',
  'destination_ready',
  'committed',
  'finalized',
  'expired',
] as const;

export type OpenWorldHandoffState = (typeof OPEN_WORLD_HANDOFF_STATES)[number];

export const openWorldHandoffStateSchema = z.enum(OPEN_WORLD_HANDOFF_STATES);

/** Whether the destination owns the hull once a handoff has reached this state. */
export function openWorldHandoffIsCommitted(state: OpenWorldHandoffState): boolean {
  return state === 'committed' || state === 'finalized';
}

/**
 * Why a crossing was refused. A code, so a caller acts without parsing prose.
 *
 * Each one is a different fact and none of them is a retry hint: a client that
 * asked to leave through the edge of the world gets `no_neighbour` however many
 * times it asks.
 */
export const OPEN_WORLD_CROSSING_REFUSALS = [
  /** The world ends in that direction. `F/10` has no east. */
  'no_neighbour',
  /**
   * One of the two quadrants is a safe port.
   *
   * Refused **explicitly** rather than by omission: entering a port has rules of
   * its own - who may dock, what protection means, what happens to a pursuer -
   * and none of them is decided. A crossing that silently worked would be those
   * rules invented by accident.
   */
  'safe_port',
  /** The hull is not inside the border band of the edge it asked for. */
  'not_at_border',
  /** This account already has a handoff in flight. One at a time. */
  'already_crossing',
  /**
   * The coordinator does not agree that this account is where the caller says.
   *
   * The fence. A room that was rebuilt from a stale record, or a socket that
   * survived a crossing, is refused by this and never by a guess.
   */
  'wrong_epoch',
  /** A wreck does not travel. */
  'destroyed',
  /** The destination could not be reached or would not reserve. */
  'destination_unavailable',
  /** The reservation ran out before anybody committed. */
  'expired',
  /**
   * The destination is full.
   *
   * **Produced since V28I.AD** (ADR 0092). The destination counts hulls plus
   * live reservations against the configured quadrant capacity inside one
   * indivisible step, and answers this BEFORE the linearisation point — so a
   * crossing that does not fit leaves the hull in the origin with nothing
   * changed, and an import that has already committed is never refused for it.
   *
   * The NUMBER is still the closing benchmark's decision; what exists is the
   * boundary that enforces whatever it turns out to be.
   */
  'capacity',
  /** The room or the coordinator could not vouch for itself. */
  'unavailable',
] as const;

export type OpenWorldCrossingRefusal = (typeof OPEN_WORLD_CROSSING_REFUSALS)[number];

export const openWorldCrossingRefusalSchema = z.enum(OPEN_WORLD_CROSSING_REFUSALS);

/* -------------------------------------------------------------------------- */
/*  The token                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How many bytes of Web Crypto entropy a handoff token carries: **32**.
 *
 * A token is what lets a client open the destination's socket for a crossing
 * that has been committed, and nothing else in this system will accept it. It
 * is therefore a capability, and a guessable capability is a way into somebody
 * else's chair - so it is drawn the same way every unguessable value in this
 * repository is, and never from `Math.random`.
 */
export const OPEN_WORLD_HANDOFF_TOKEN_BYTES = 32;

/** Lower-case hex, exactly the declared length. Refused, never repaired. */
export const openWorldHandoffTokenSchema = z
  .string()
  .regex(new RegExp(`^[0-9a-f]{${OPEN_WORLD_HANDOFF_TOKEN_BYTES * 2}}$`));

/**
 * How long a reservation stands before it is worth nothing: **twenty seconds**.
 *
 * Long enough for a client to open a second socket over a bad connection, short
 * enough that a quadrant is not holding reservations for people who changed
 * their mind. It bounds the whole uncommitted half of the machine: everything
 * before `committed` is abandoned by this expiry and by nothing else.
 *
 * **Provisional**, like every other number in this feature, until the closing
 * benchmark measures what a real second connection costs.
 */
export const OPEN_WORLD_HANDOFF_TTL_MS = 20_000;

/**
 * How long the origin remembers that an account LEFT: **five minutes**.
 *
 * The fence. While it stands, the origin refuses to seat that account again,
 * so a socket that survived the crossing - or a retried admission - cannot
 * produce a second attackable hull in the quadrant the pilot has left.
 *
 * It is deliberately not forever. A fence outlives the confusion it exists to
 * settle and nothing more: after five minutes every socket that could have been
 * in flight is long gone, and keeping the record would make a quadrant's
 * storage grow with every pilot that ever passed through it.
 */
export const OPEN_WORLD_DEPARTURE_FENCE_MS = 5 * 60_000;

/* -------------------------------------------------------------------------- */
/*  What a client says, and what it is told                                   */
/* -------------------------------------------------------------------------- */

/**
 * A handoff's identity. Minted by the coordinator, opaque to everybody else.
 *
 * A UUID and not a counter: a counter would tell a client how many crossings
 * the world has done, and a handoff id travels to two rooms and one browser.
 */
export const openWorldHandoffIdSchema = z.uuid();

/**
 * The idempotency key a client puts on its crossing intent.
 *
 * The same role the hunt's `commandId` has: a client that does not see its
 * request take effect retries, and a retry must be READ rather than start a
 * second reservation. The sequence number already stops a replayed FRAME; this
 * stops a re-sent INTENT, which is a different thing and is what an honest
 * client does when it is unsure.
 */
export const openWorldCommandIdSchema = z.uuid();

/**
 * **The crossing intent, and it carries three things.**
 *
 * A direction, this order's identity, and the connection's ordinary sequence.
 * There is no destination coordinate, no arrival position, no participant id,
 * no hull state, no damage confirmation and no room name - and there is nowhere
 * to put one. The origin derives every one of those from what it already holds,
 * which is what makes "the client cannot choose where it lands" a property of
 * the schema rather than of a check.
 */
export const openWorldCrossingIntentSchema = z
  .object({
    t: z.literal('x'),
    /** The connection's own counter, exactly as an ordinary command carries it. */
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    /** Which way. Four values, and never a diagonal. */
    d: openWorldDirectionSchema,
    /** This order's identity, repeated on every retry. */
    id: openWorldCommandIdSchema,
  })
  .strict();

export type OpenWorldCrossingIntent = z.infer<typeof openWorldCrossingIntentSchema>;

/**
 * **What the origin tells the client about a crossing it asked for.**
 *
 * One frame per state change, addressed to the connection that asked. It
 * carries the token ONLY once the destination has reserved - before that there
 * is nothing to hand over, and after a commit the token has already been spent.
 *
 * The destination coordinate travels because the client has to know which room
 * to open. It is derived by the server; the client never proposed it.
 */
export const openWorldCrossingResultSchema = z
  .object({
    t: z.literal('xr'),
    /** The order this answers. The same id the client sent. */
    id: openWorldCommandIdSchema,
    /** Where the machine is now, or `null` when the answer is a refusal. */
    s: openWorldHandoffStateSchema.nullable(),
    /** Why not, when it is a refusal. Null when the answer is a state. */
    r: openWorldCrossingRefusalSchema.nullable(),
    /**
     * Where the crossing leads. Present from `destination_ready` onwards.
     *
     * Indices and never the `F/10` caption: a label is a rendering, and parsing
     * a rendering is how two sides end up disagreeing about which quadrant they
     * meant.
     */
    q: z
      .object({
        rowIndex: z.number().int().min(0),
        columnIndex: z.number().int().min(0),
      })
      .strict()
      .optional(),
    /** The single-use capability that opens the destination's socket. */
    k: openWorldHandoffTokenSchema.optional(),
    /** When the reservation stops being worth anything, in milliseconds from now. */
    ttl: z.number().int().min(0).optional(),
  })
  .strict();

export type OpenWorldCrossingResult = z.infer<typeof openWorldCrossingResultSchema>;
